/**
 * Storages for `atomWithStorage` over `WebStorage`, which never throws. Jotai's own JSON storage
 * catches only a refused `window.localStorage`: a quota error leaves its `setItem` and escapes
 * the write of the atom.
 */

import type { atomWithStorage } from 'jotai/utils';

import type { WebStorage } from '@/lib/webStorage';

type Storage<V> = NonNullable<Parameters<typeof atomWithStorage<V>>[2]>;

/** A value in JSON. `parse` makes a value from what is stored, whatever that is. */
export function jsonStorage<V>(
    store: WebStorage, parse: (stored: unknown, initial: V) => V,
): Storage<V> {
    return {
        getItem: (key, initial): V => parse(store.readJson(key), initial),
        setItem: (key, value): void => store.writeJson(key, value),
        removeItem: (key): void => store.remove(key),
    };
}

/** A string, or null while nothing is stored. */
export function textStorage(store: WebStorage): Storage<string | null> {
    return {
        getItem: (key): string | null => store.readText(key),
        setItem: (key, value): void => {
            if (value === null) {
                store.remove(key);
            } else {
                store.writeText(key, value);
            }
        },
        removeItem: (key): void => store.remove(key),
    };
}
